import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  LayoutTemplate,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { cn } from '@/core/lib/utils';
import { getPaletteColor } from '@/core/lib/colors';
import { useProjectsInfinite } from '@/shared/hooks/useProjectsInfinite';
import { ProjectFormDialog } from '@/features/projects/public';
import type { Project } from '@/features/projects/public';
import { BoardFormDialog } from '@/features/boards/public';
import { SaveAsTemplateDialog } from '@/features/project-templates/public';
import { BoardSubList } from './BoardSubList';

export function SidebarProjects() {
  const { t } = useTranslation('layout');
  const { orgId } = useParams<{ orgId: string }>();

  const [isExpanded, setIsExpanded] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | undefined>(undefined);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProjectsInfinite();

  const projects = data?.pages.flatMap((p) => p.items ?? []) ?? [];

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setDialogOpen(true);
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col px-3 py-3">
        <div className="mb-2 flex shrink-0 items-center justify-between px-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('sidebar.projects.title')}
          </span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded((v) => !v)}
                className="h-6 w-6"
                data-testid="projects-sidebar-toggle"
                aria-label={
                  isExpanded ? t('sidebar.projects.collapse') : t('sidebar.projects.expand')
                }
              >
                {isExpanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isExpanded ? t('sidebar.projects.collapse') : t('sidebar.projects.expand')}
            </TooltipContent>
          </Tooltip>
        </div>

        {isExpanded && (
          <div
            className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto"
            data-testid="projects-sidebar-list"
          >
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2.5">
                  <Skeleton className="h-2 w-2 shrink-0 rounded-full" />
                  <Skeleton className="h-3 flex-1" />
                </div>
              ))
            ) : isError ? (
              <p
                className="px-2 py-2 text-xs text-destructive"
                data-testid="projects-sidebar-error"
              >
                {t('sidebar.projects.error')}
              </p>
            ) : projects.length === 0 ? (
              <p
                className="px-2 py-2 text-xs text-muted-foreground"
                data-testid="projects-sidebar-empty"
              >
                {t('sidebar.projects.empty')}
              </p>
            ) : (
              <>
                {projects.map((project) => (
                  <ProjectItem
                    key={project.id}
                    project={project}
                    orgId={orgId!}
                    onEdit={handleEdit}
                    t={t}
                  />
                ))}

                {hasNextPage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="mt-1 w-full justify-start px-2 text-xs text-muted-foreground"
                    data-testid="projects-sidebar-load-more"
                  >
                    {isFetchingNextPage
                      ? t('sidebar.projects.expand')
                      : t('sidebar.projects.loadMore')}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ProjectFormDialog open={dialogOpen} onOpenChange={setDialogOpen} project={editingProject} />
    </>
  );
}

interface ProjectItemProps {
  project: Project;
  orgId: string;
  onEdit: (project: Project) => void;
  t: ReturnType<typeof useTranslation<'layout'>>['t'];
}

function ProjectItem({ project, orgId, onEdit, t }: ProjectItemProps) {
  const colorValue = project.color ? getPaletteColor(project.color)?.value : undefined;
  const [boardsExpanded, setBoardsExpanded] = useState(false);
  const [createBoardOpen, setCreateBoardOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);

  return (
    <div>
      <div className="group flex items-center rounded-lg transition-colors hover:bg-muted">
        <button
          type="button"
          onClick={() => setBoardsExpanded((v) => !v)}
          className="flex flex-1 min-w-0 items-center gap-2 px-2 py-2 text-left"
        >
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground transition-transform',
              boardsExpanded && 'rotate-90',
            )}
          />
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: colorValue ?? 'hsl(var(--muted-foreground))' }}
          />
          <span className="truncate text-sm leading-none text-foreground">
            {project.name ?? '—'}
          </span>
        </button>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mr-1 h-6 w-6 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                  aria-label={t('sidebar.projects.options')}
                  data-testid={`projects-sidebar-options-${project.id}`}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('sidebar.projects.options')}</TooltipContent>
          </Tooltip>

          <DropdownMenuContent align="end" side="right">
            <DropdownMenuItem className="gap-2" onClick={() => onEdit(project)}>
              <Pencil className="h-3.5 w-3.5 shrink-0" />
              {t('sidebar.projects.edit')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2"
              onClick={() => {
                setBoardsExpanded(true);
                setCreateBoardOpen(true);
              }}
              data-testid={`projects-sidebar-add-board-${project.id}`}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              {t('sidebar.projects.addBoard')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2"
              onClick={() => setSaveAsTemplateOpen(true)}
              data-testid={`sidebar-projects-save-as-template-menu-item-${project.id}`}
            >
              <LayoutTemplate className="h-3.5 w-3.5 shrink-0" />
              {t('sidebar.projects.saveAsTemplate')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {boardsExpanded && <BoardSubList project={project} orgId={orgId} t={t} />}

      <BoardFormDialog
        open={createBoardOpen}
        onOpenChange={setCreateBoardOpen}
        projectId={project.id}
      />

      {project.id && (
        <SaveAsTemplateDialog
          projectId={project.id}
          projectName={project.name ?? ''}
          open={saveAsTemplateOpen}
          onOpenChange={setSaveAsTemplateOpen}
        />
      )}
    </div>
  );
}
